import type { ReactNode } from "react";
import clsx from "clsx";
import { HOME_MUTED_TEXT_CLASS } from "./homeSurfaces";
import {
  HomeSurfaceTable,
  HomeSurfaceTableScroll,
} from "./HomeSurfaceTable";
import LoadingPlaceholder from "@/components/ui/loading-placeholder/LoadingPlaceholder";
import { WorkbenchSearchInput } from "@/components/ui/workbench/WorkbenchSearchInput";

export type HomeListTableProps = {
  children: ReactNode;
  testId?: string;
  searchQuery?: string;
  onSearchQueryChange?: (value: string) => void;
  searchPlaceholder?: string;
  toolbarTrailing?: ReactNode;
  showSearch?: boolean;
  loading?: boolean;
  empty?: boolean;
  emptyMessage?: string;
  footer?: ReactNode;
  stickyHeader?: boolean;
  tableMinWidth?: string;
  scrollClassName?: string;
};

/**
 * Standard home / surface list table chrome — search toolbar, inset scroll panel,
 * and optional footer. Pair with `HomeSurfaceTable*` primitives for headers and rows.
 *
 * Used by Needs you (`HomeTaskConsoleTable`) and onboarding stat drill dialogs.
 */
export function HomeListTable({
  children,
  testId = "home-list-table",
  searchQuery,
  onSearchQueryChange,
  searchPlaceholder = "Search…",
  toolbarTrailing,
  showSearch = true,
  loading = false,
  empty = false,
  emptyMessage = "No items to show.",
  footer,
  tableMinWidth,
  scrollClassName,
}: HomeListTableProps) {
  if (loading && empty) {
    return <LoadingPlaceholder height="12rem" width="100%" />;
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4" data-testid={testId}>
      {showSearch && onSearchQueryChange ? (
        <div className="flex items-center gap-2">
          <WorkbenchSearchInput
            className="min-w-0 flex-1"
            value={searchQuery ?? ""}
            onChange={onSearchQueryChange}
            placeholder={searchPlaceholder}
            data-testid={`${testId}-search`}
          />
          {toolbarTrailing}
        </div>
      ) : null}

      {empty ? (
        <p className={HOME_MUTED_TEXT_CLASS}>{emptyMessage}</p>
      ) : (
        <HomeSurfaceTableScroll
          className={scrollClassName}
          data-testid={`${testId}-scroll`}
        >
          <HomeSurfaceTable minWidth={tableMinWidth}>{children}</HomeSurfaceTable>
        </HomeSurfaceTableScroll>
      )}

      {footer}
    </div>
  );
}
